import React, { useState } from "react";
import { MailOutlined, PhoneOutlined } from "@ant-design/icons";
import { Pagination } from "antd5";
import classNames from "classnames";

import { DetailsContent } from "components/app_layout/DetailsLayout";
import Button from "components/ui/button/Button";
import { EllipsisTooltipText } from "lib/core_components/EllipsisTooltip";
import RedactedWrapper, { RedactedLink } from "lib/core_components/RedactedWrapper";
import SkeletonTable from "lib/core_components/SkeletonTable";
import { Table } from "lib/core_components/Table";
import { ColumnSizeConfig, ColumnType } from "lib/core_components/Table/ColumnTypes";
import { tableRowSelection } from "lib/core_components/Table/Table";
import { createUseDebounce, useDebouncedValue } from "lib/debounce";
import { ContactResult, useContacts } from "lib/hooks/api/contacts/useContacts";
import { NoticeDetails } from "lib/hooks/api/notices/useNotice";
import { TableKey, useTablePageSize } from "lib/hooks/useTablePageSize";
import { useURLState } from "lib/hooks/useURLState";
import { useCheckSubscription } from "lib/providers/ProHelper";
import { TrackingProvider } from "lib/tracking";
import { Flex } from "../../styles/utility-components";
import { SaveLeadDrawerProvider } from "../leads/saving/SaveLeadDrawerProvider";
import { PaywallBanner } from "../paywall/PaywallBanner";
import ContactsActionBar from "./ContactsActionBar";
import { ContactsFilterBar } from "./ContactsFilterBar";
import {
  BuyerDetailsCell,
  ContactInformationCell,
  ContactNameCell,
  ContactsTableEmpty,
} from "./ContactsTableUtils";
import {
  ContactFilters,
  convertFiltersToSearchContactsRequest,
  DEFAULT_CONTACT_FILTERS,
  parseContactsFilters,
} from "./contactUtils";

import css from "./ContactsTable.module.scss";

const CONTACTS_DATA_TYPE = "CONTACTS";

const BASIC_SIZE: ColumnSizeConfig = {
  small: 150,
  medium: 150,
  large: 175,
  xlarge: 350,
};

const MEDIUM_SIZE: ColumnSizeConfig = {
  small: 200,
  medium: 225,
  large: 225,
  xlarge: 500,
};

const LARGE_SIZE: ColumnSizeConfig = {
  small: 350,
  medium: 350,
  large: 350,
  xlarge: 500,
};

type ContactsTableProps = {
  // If this prop is provided, then contacts are searched for already within thr context of a buyer
  buyerId?: string;
  addContacts?: (contacts: ContactResult[]) => void;

  // Want to make this prop required so we have complete tracking of this reusable component
  contextSource: string;
  tableKey: TableKey;
  pageSize?: number;

  showAllFiltersDrawer: boolean;
  showDropdownFilters: boolean;

  notice?: NoticeDetails;
};

function ContactsTable({
  buyerId,
  addContacts,
  contextSource,
  tableKey,
  pageSize: defaultPageSize = 20,
  showAllFiltersDrawer,
  showDropdownFilters,
  notice,
}: ContactsTableProps) {
  const { authorised: hasContacts } = useCheckSubscription("CONTACTS", {
    "Context source": contextSource,
  });

  const useDebounce300 = createUseDebounce(300);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useTablePageSize(tableKey, defaultPageSize);

  // If we are within the context of a buyer, then the default filters must always reflect this
  const defaultContactFilters: ContactFilters = {
    ...DEFAULT_CONTACT_FILTERS,
    buyerGuids: buyerId ? [buyerId] : [],
  };

  const [contactFilters = defaultContactFilters, setContactFilters] = useURLState<ContactFilters>(
    "contactsFilters",
    defaultContactFilters,
    (params) => parseContactsFilters(params, defaultContactFilters),
  );

  const [selectedRowGuids, setSelectedRowGuids] = useState<string[]>([]);

  const [debouncedContactFilters] = useDebouncedValue<ContactFilters>(contactFilters);

  const [showTooltipRowIndex, setShowTooltipRowIndex] = useState<number>();

  const {
    data: contactsData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useContacts(
    {
      ...convertFiltersToSearchContactsRequest(debouncedContactFilters, pageSize, currentPage),
    },
    {
      enabled: hasContacts,
    },
  );

  const onChangeSearchFilters = useDebounce300((filters: Partial<ContactFilters>) => {
    setContactFilters((oldFilters) => ({ ...oldFilters, ...filters }));
    setCurrentPage(1);
  });

  const columns = React.useMemo((): ColumnType<ContactResult>[] => {
    const columns: ColumnType<ContactResult>[] = [
      {
        key: "name",
        title: "Job title & Contact name",
        render: (t: unknown, c: ContactResult, index: number) => (
          <ContactNameCell
            contact={c}
            notice={notice}
            rowIndex={index}
            showTooltipRowIndex={showTooltipRowIndex}
            setShowTooltipRowIndex={setShowTooltipRowIndex}
            showFirstCellActions={!!notice}
          />
        ),
        sizeConfig: LARGE_SIZE,
      },
      {
        title: <EllipsisTooltipText fullText="Contact information" />,
        key: "contactInformation",
        sizeConfig: MEDIUM_SIZE,
        render: (_: unknown, c: ContactResult) => (
          <RedactedWrapper
            requiredDataType={CONTACTS_DATA_TYPE}
            contextSource="In-row"
            redactContent={
              <div className={css.contactCellContent}>
                <span className={css.contactDetail}>
                  <MailOutlined className={css.contactIcon} />
                  <RedactedLink textToRedact={c.email || "john@fakemail.com"} />
                </span>
                <span className={css.contactDetail}>
                  <PhoneOutlined className={css.contactIcon} />
                  <RedactedLink textToRedact={c.phone || "123-456 78911"} />
                </span>
              </div>
            }
          >
            <ContactInformationCell contact={c} />
          </RedactedWrapper>
        ),
      },
      {
        title: "Seniority",
        key: "seniority",
        dataIndex: "seniority",
        sizeConfig: BASIC_SIZE,
        render: (_: unknown, c: ContactResult) => (
          <EllipsisTooltipText fullText={c.seniority} containerClassname={css.elipsisText} />
        ),
      },
      {
        title: "Function",
        key: "function",
        dataIndex: "jobFunction",
        sizeConfig: BASIC_SIZE,
        render: (_: unknown, c: ContactResult) => (
          <EllipsisTooltipText
            fullText={c.jobFunction.join(", ")}
            containerClassname={css.elipsisText}
          />
        ),
      },
    ];

    if (!buyerId) {
      columns.splice(2, 0, {
        title: "Buyer",
        key: "buyerGuid",
        render: (_: unknown, c: ContactResult) => {
          if (!c.buyer) {
            return <span>{c.oscarOrgName}</span>;
          }

          return <BuyerDetailsCell buyer={c.buyer} />;
        },
        sizeConfig: MEDIUM_SIZE,
      });
    }

    return columns;
  }, [buyerId, notice, showTooltipRowIndex]);

  if (!hasContacts || (error instanceof Error && error.message.includes("Permission error"))) {
    return <PaywallBanner featureType="CONTACTS" />;
  }

  return (
    <TrackingProvider
      data={{
        "Context source": contextSource,
      }}
    >
      <span>
        {addContacts && (
          <Button
            buttonStyle="destructive"
            disabled={selectedRowGuids.length === 0}
            onClick={() =>
              addContacts(
                contactsData?.contacts.results.filter((x) => selectedRowGuids.includes(x.id)) || [],
              )
            }
          >
            Add Contacts ({selectedRowGuids.length})
          </Button>
        )}
        <ContactsFilterBar
          contactFilters={contactFilters}
          onChangeSearchFilters={onChangeSearchFilters}
          selectedRowGuids={selectedRowGuids}
          totalResults={contactsData?.contacts.totalResults ?? 0}
          hideBuyerFilters={!!buyerId}
          defaultFilters={defaultContactFilters}
          showAllFiltersDrawer={showAllFiltersDrawer}
          showDropdownFilters={showDropdownFilters}
          paginationState={{ current: currentPage, pageSize }}
        />
      </span>
      <DetailsContent
        className={classNames({
          [css.removeSpacing]: !!buyerId,
        })}
      >
        <Flex column gap={16}>
          {selectedRowGuids.length > 0 && (
            <ContactsActionBar
              selectedIds={selectedRowGuids}
              onClearSelection={() => setSelectedRowGuids([])}
            />
          )}
          {isLoading ? (
            <SkeletonTable columns={columns} pageSize={pageSize} rowCount={pageSize} active />
          ) : contactsData?.contacts && contactsData.contacts.results.length > 0 ? (
            <SaveLeadDrawerProvider
              trackingData={{
                "Context source": "In row",
              }}
            >
              <Table<ContactResult>
                ariaLabel="Contacts Table"
                columns={columns}
                dataSource={contactsData?.contacts.results || []}
                loading={isFetching}
                className={css.hiddenOverflow}
                rowKey={(row) => row.id}
                pagination={false}
                rowSelection={tableRowSelection(
                  selectedRowGuids,
                  setSelectedRowGuids,
                  contactsData?.contacts.results || [],
                  "id",
                )}
                isError={isError}
              />
              <Pagination
                className={css.pagination}
                current={currentPage}
                defaultPageSize={pageSize}
                total={contactsData?.contacts.totalResults ? contactsData.contacts.totalResults : 0}
                onChange={(current: number, pageSize: number) => {
                  setCurrentPage(current);
                  setPageSize(pageSize);
                }}
              />
            </SaveLeadDrawerProvider>
          ) : (
            <ContactsTableEmpty />
          )}
        </Flex>
      </DetailsContent>
    </TrackingProvider>
  );
}

export default ContactsTable;
